import { store } from '@/app/store';
import config from '@/config';
import { selectInvitePermissions } from '@/features/invite/model/selectors';
import timeSync from '@/infrastructure/utils/timeSync';
import { MESSAGE_PROCESSING_STATUS_ID } from '@/shared/constants';
import { getLocalUserId } from '@/shared/utils';
import { createSelector } from '@reduxjs/toolkit';
import { shallowEqual, useSelector } from 'react-redux';

/**
 * Conversation Selectors + Hooks
 *
 * All selectors and React hooks for the conversation slice live here.
 * reducer.js contains only the slice definition, actions, and reducer export.
 */

const EMPTY_ARRAY = [];

// ─── Plain selectors (state accessors) ────────────────────────────────────────

const selectMessages = (state, chatId) => state.conversationData.data[chatId]?.messages || EMPTY_ARRAY;

const selectSelectionType = (state, chatId) => state.conversationData.data[chatId]?.selectionType || '';

export const selectMessageById = (state, chatId, msgId) => {
   if (!chatId || !msgId) return null;
   const messages = selectMessages(state, chatId);
   for (const m of messages) {
      if (m.msgId === msgId) return m;
      // Search inside groupedMedia — fast includes() pre-check then exact child match
      if (m.type === 'groupedMedia' && m.msgId?.includes(msgId)) {
         const child = m.messages?.find(c => (c.msgId || c.messageId) === msgId);
         if (child) return child;
      }
   }
   return null;
};

// ─── Store-direct helpers (non-React, use sparingly) ──────────────────────────

export const getConversationMessages = chatId => store.getState().conversationData.data[chatId]?.messages || [];

export const getMessageSelectionCount = chatId => {
   const messages = store.getState().conversationData.data[chatId]?.messages || [];
   const selectionType = store.getState().conversationData.data[chatId]?.selectionType || '';

   let count = 0;
   for (const m of messages) {
      if (!m.isSelected) continue;
      if (m.type === 'groupedMedia') {
         count += m.messages?.length || 0;
      } else {
         count += 1;
      }
   }
   return { selectionCount: count, selectionType };
};

export const getIsAnyMessageSelected = chatId => {
   const messages = store.getState().conversationData.data[chatId]?.messages || [];
   return messages.some(m => m.isSelected || (m.type === 'groupedMedia' && (m.messages || []).some(c => c.isSelected)));
};

export const getSelectedMessages = chatId => {
   const messages = store.getState().conversationData.data[chatId]?.messages || [];
   const selected = [];
   for (const m of messages) {
      if (m.type === 'groupedMedia') {
         for (const c of m.messages || []) {
            if (c.isSelected) selected.push(c);
         }
      } else if (m.isSelected) {
         selected.push(m);
      }
   }
   selected.sort((a, b) => b.timestamp - a.timestamp);
   return selected;
};

/**
 * Collect every still-unsent leaf message (sand-timer, msgStatus === PROCESSING)
 * for a chat, flattening groupedMedia wrappers into their children. Returned
 * newest-first, mirroring getSelectedMessages. Used on reconnect to retain
 * messages that never reached the server.
 */
export const getUnsentMessages = chatId => {
   const messages = store.getState().conversationData.data[chatId]?.messages || [];
   const unsent = [];
   for (const m of messages) {
      if (m.type === 'date-label' || m.type === 'unread-separator') continue;
      if (m.type === 'groupedMedia') {
         for (const c of m.messages || []) {
            if (c.msgStatus === MESSAGE_PROCESSING_STATUS_ID && !c.deleteStatus) unsent.push(c);
         }
      } else if (m.msgStatus === MESSAGE_PROCESSING_STATUS_ID && !m.deleteStatus) {
         unsent.push(m);
      }
   }
   unsent.sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0));
   return unsent;
};

export const getMessageFromStore = (chatId, msgId) => {
   const messages = store.getState().conversationData.data[chatId]?.messages || [];
   for (const m of messages) {
      if (m.editMessageId === msgId || m.msgId === msgId) return m;
      // Search inside groupedMedia — fast includes() pre-check then exact child match
      if (m.type === 'groupedMedia' && (m.msgId?.includes(msgId) || m.editMessageId?.includes(msgId))) {
         const child = m.messages?.find(c => c.editMessageId === msgId || c.msgId === msgId);
         if (child) return child;
      }
   }
   return null;
};

export const isAnyMessageWithinDeleteDuration = (messages = []) => {
   const now = timeSync.getSyncedTime();
   const localUserId = getLocalUserId();
   const durationMs = (config.deleteForEveryOneDurationInSec || 30) * 1000;

   return messages.every(message => {
      const msgTime = message.timestamp || message.msgTime;
      if (!msgTime) return false;
      const timestampMs = msgTime < 10000000000 ? msgTime * 1000 : msgTime;
      const isWithinTime = now - timestampMs <= durationMs;
      const isFromMe = message.fromUserId === localUserId;
      return isWithinTime && isFromMe && !message?.deleteStatus;
   });
};

// ─── Memoized createSelector ──────────────────────────────────────────────────

const getSelectionInfo = createSelector([selectMessages, selectSelectionType], (messages, selectionType) => {
   let count = 0;
   for (const m of messages) {
      if (m.type === 'groupedMedia') {
         for (const c of m.messages || []) {
            if (c.isSelected) count += 1;
         }
      } else if (m.isSelected) {
         count += 1;
      }
   }
   return {
      selectionCount: count,
      selectionType,
   };
});

// ─── React hooks ─────────────────────────────────────────────────────────────

export const useMessageSelectionCount = chatId => useSelector(state => getSelectionInfo(state, chatId), shallowEqual);

export const useIsAnyMessageSelected = chatId =>
   useSelector(state =>
      (state.conversationData.data[chatId]?.messages || []).some(
         m => m.isSelected || (m.type === 'groupedMedia' && (m.messages || []).some(c => c.isSelected)),
      ),
   );

export const useMessageById = (chatId, msgId) => useSelector(state => selectMessageById(state, chatId, msgId));

const selectAllSelectedStarred = createSelector([selectMessages], messages => {
   const selected = [];
   for (const m of messages) {
      if (m.type === 'groupedMedia') {
         for (const c of m.messages || []) {
            if (c.isSelected) selected.push(c);
         }
      } else if (m.isSelected) {
         selected.push(m);
      }
   }
   return selected.length > 0 && selected.every(m => m.favouriteStatus === 1);
});

export const useAllSelectedStarred = chatId => useSelector(state => selectAllSelectedStarred(state, chatId));

export const useConversationInviteInfo = jid => useSelector(state => selectInvitePermissions(state, jid), shallowEqual);
